import { CSSProperties, useEffect, useReducer, useState } from 'react';
import useAuth from '@/src/Authentication/components/hooks/useAuth';
import { API_URL } from '@/src/services/api';
import {
    OVERRIDES_EVENT,
    V3_AREAS,
    getWorldOverride,
    isNative,
    setWorldOverride,
} from './flags';

/*
 * Bottom dev strip (ref: Linear's internal toolbar — DESIGN-BRIEF.md,
 * process principle 4). GlobalAdmin + local dev only. One WORLD toggle
 * (classic ↔ v3 for this browser only) plus per-area native/bridge
 * status — the compare loop for a reconfigured IA.
 */

const ENV_LABEL = API_URL.includes('alpha')
    ? 'ALPHA'
    : API_URL.includes('localhost')
      ? 'LOCAL'
      : 'PROD';

const bar: CSSProperties = {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    height: 26,
    padding: '0 10px',
    background: '#0f1218',
    color: '#98a1af',
    fontFamily: '"SF Mono", ui-monospace, Menlo, monospace',
    fontSize: 10,
    borderTop: '1px solid #232a34',
};
const chip: CSSProperties = {
    border: 0,
    background: 'none',
    color: 'inherit',
    font: 'inherit',
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: 4,
};

export default function DevToolbar() {
    const { auth } = useAuth();
    const [hidden, setHidden] = useState(
        () => window.sessionStorage.getItem('ps.devbar.hidden') === '1',
    );
    const [, bump] = useReducer((x: number) => x + 1, 0);

    useEffect(() => {
        const onChange = () => bump();
        window.addEventListener(OVERRIDES_EVENT, onChange);
        return () => window.removeEventListener(OVERRIDES_EVENT, onChange);
    }, []);

    const isGlobalAdmin =
        Array.isArray(auth?.roles) && auth.roles.indexOf(69) > -1;
    const isDev = process.env.NODE_ENV === 'development';
    /* not inside /preview's device frames — it's a desktop tool */
    const framed = window.self !== window.top;
    const visible = (isGlobalAdmin || isDev) && !hidden && !framed;

    /* Fixed chrome (the v3 shell) insets itself by this so the bar
       never covers it (e.g. the rail's account row). */
    useEffect(() => {
        document.body.style.setProperty(
            '--ps-devbar',
            visible ? '26px' : '0px',
        );
        return () => {
            document.body.style.removeProperty('--ps-devbar');
        };
    }, [visible]);

    if (!visible) return null;

    const world = getWorldOverride();
    /* auto -> v3 -> classic -> auto */
    const cycleWorld = () =>
        setWorldOverride(world === undefined ? true : world ? false : null);
    const worldLabel =
        world === undefined ? 'WORLD auto' : world ? 'WORLD V3' : 'WORLD CLASSIC';
    const worldColor =
        world === undefined ? '#98a1af' : world ? '#49b571' : '#f0895f';

    return (
        <div style={bar}>
            <span style={{ color: '#e8ebf1', fontWeight: 700 }}>PS·DEV</span>
            <span>{ENV_LABEL}</span>
            <span style={{ opacity: 0.6 }}>
                {API_URL.replace(/^https?:\/\//, '')}
            </span>
            <button
                style={{ ...chip, color: worldColor, fontWeight: 700 }}
                title="Flip this browser between classic and the v3 world"
                onClick={cycleWorld}
            >
                {worldLabel}
            </button>
            <a
                style={{ ...chip, color: '#98a1af', textDecoration: 'none' }}
                href="/kit"
                title="The v3 kit — the vocabulary rendered by the real components"
            >
                KIT
            </a>
            <a
                style={{ ...chip, color: '#98a1af', textDecoration: 'none' }}
                href="/rules"
                title="Draft design rules, rendered live"
            >
                RULES
            </a>
            <a
                style={{ ...chip, color: '#98a1af', textDecoration: 'none' }}
                href="/preview"
                title="Any page at phone, narrow-window, and tablet sizes"
            >
                PREVIEW
            </a>
            <span style={{ flex: 1 }} />
            {V3_AREAS.length === 0 ? (
                <span style={{ opacity: 0.6 }}>no v3 areas registered</span>
            ) : (
                V3_AREAS.map((a) => (
                    <span
                        key={a.id}
                        style={{
                            ...chip,
                            cursor: 'default',
                            color: isNative(a.id) ? '#49b571' : '#f0895f',
                        }}
                        title={
                            isNative(a.id)
                                ? `${a.route} — native`
                                : `bridges to ${a.classicUrl ?? 'classic'}`
                        }
                    >
                        {a.label} {isNative(a.id) ? 'native' : 'bridge'}
                    </span>
                ))
            )}
            <button
                style={chip}
                title="Hide for this session"
                onClick={() => {
                    window.sessionStorage.setItem('ps.devbar.hidden', '1');
                    setHidden(true);
                }}
            >
                ×
            </button>
        </div>
    );
}
